// Speedbump modal: intercepts external link clicks and shows a confirmation modal.
// Trigger: any <a> inside the page whose href points to an off-site domain not in the whitelist.
// Modal source: a ModalSection rendered with data-modal-behavior="speedbump".
import { Modal } from 'bootstrap'

interface SpeedbumpModal {
  el: HTMLElement
  whitelist: string[]
  modal: Modal
  confirmBtn: HTMLAnchorElement | null
}

function hostMatches(linkHost: string, pattern: string): boolean {
  pattern = pattern.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase()
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(1) // ".example.com"
    return linkHost === pattern.slice(2) || linkHost.endsWith(suffix)
  }
  return linkHost === pattern || linkHost.endsWith('.' + pattern)
}

function shouldIntercept(href: string, whitelist: string[]): boolean {
  if (!href) return false
  // Skip non-navigational links and same-origin
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return false
  let url: URL
  try {
    url = new URL(href, window.location.href)
  } catch {
    return false
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
  if (url.host === window.location.host) return false
  const linkHost = url.host.toLowerCase()
  // Always whitelist the current host implicitly (handled above), and explicit whitelist entries.
  return !whitelist.some(p => hostMatches(linkHost, p))
}

document.addEventListener('DOMContentLoaded', () => {
  const speedbumps: SpeedbumpModal[] = Array.from(
    document.querySelectorAll<HTMLElement>('[data-modal-behavior="speedbump"]')
  ).map(el => {
    let whitelist: string[] = []
    try {
      whitelist = JSON.parse(el.getAttribute('data-whitelist') || '[]')
    } catch {
      whitelist = []
    }
    return {
      el,
      whitelist,
      modal: Modal.getOrCreateInstance(el),
      confirmBtn: el.querySelector<HTMLAnchorElement>('[data-modal-action="confirm"]'),
    }
  })

  if (speedbumps.length === 0) return

  // Use a single primary speedbump (the first registered) for site-wide interception.
  const primary = speedbumps[0]

  document.body.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement | null
    if (!target) return
    const anchor = target.closest('a') as HTMLAnchorElement | null
    if (!anchor) return
    // Skip anchors that already trigger Bootstrap modals, and the confirm button itself.
    if (anchor.getAttribute('data-bs-toggle') === 'modal') return
    if (anchor.getAttribute('data-modal-action') === 'confirm') return
    const href = anchor.getAttribute('href') || ''
    if (!shouldIntercept(href, primary.whitelist)) return

    ev.preventDefault()
    if (primary.confirmBtn) {
      primary.confirmBtn.href = anchor.href
      const target = anchor.getAttribute('target')
      if (target) primary.confirmBtn.setAttribute('target', target)
      else primary.confirmBtn.removeAttribute('target')
      primary.confirmBtn.setAttribute('rel', 'noopener')
    }
    primary.modal.show()
  })

  // Cancel button just closes the modal.
  speedbumps.forEach(sb => {
    sb.el.querySelectorAll<HTMLElement>('[data-modal-action="cancel"]').forEach(btn => {
      btn.addEventListener('click', () => sb.modal.hide())
    })
  })
})
